import { ACTIVITY_ACTIONS, activityActionLabel } from "@/lib/activity-actions";
import { shiftDay } from "@/lib/calendar";
import { belgianDay } from "@/lib/format";
import type { Language } from "@/lib/i18n/language";
import { parsePeriod, type Period } from "@/lib/period";
import { labelOrder } from "@/lib/table-sort";
import { belgianMoment, type TimeRange, type TimeRangeUrl } from "@/lib/time-range";

/** The period the Logboek opens on: the past month. */
const LOGBOOK_DAYS: Period = 30;

/** The Logboek's time range when the page opens, and after "Filters wissen": the past month. */
export const LOGBOOK_RANGE: TimeRange = { kind: "period", days: LOGBOOK_DAYS };

/**
 * How the Logboek keeps its time range in the URL: the past month left out,
 * and a custom range beside the period (`?days=`) the figures above the log
 * keep following.
 */
export const LOGBOOK_RANGE_URL: TimeRangeUrl = { defaultDays: LOGBOOK_DAYS, keepPeriod: true };

/** The Logboek's filters in its URL, besides the time range and the sort: `?q=…&person=…&action=…`. */
export const LOGBOOK_PARAMS = { search: "q", person: "person", action: "action" } as const;

/**
 * The period of the figures above the log (the key figures, the activity over
 * time and per action): the log's time range when it is a period, else the
 * period it had before a custom range, from the URL (`?days=`), the past
 * month by default.
 */
export function logbookPeriod(params: URLSearchParams): Period {
  return parsePeriod(params.get("days"), LOGBOOK_DAYS);
}

/**
 * What the activity log's API takes for a time range: a custom range's two
 * moments, or for a period its first moment alone (00:00 in Belgian time on
 * the first of its calendar days, today included), so the log runs up to now.
 */
export function activityTimeBounds(range: TimeRange, now: Date): { from?: string; to?: string } {
  if (range.kind === "custom") {
    return { from: range.from, to: range.to };
  }
  const today = belgianDay(now);
  const from = today ? belgianMoment(shiftDay(today, -(range.days - 1)), "00:00") : null;
  return from ? { from } : {};
}

/**
 * The order the server follows for a sort on the Logboek's Actie column
 * (`sortOrder`): the actions (those in the log, `actions`, and every action
 * the texts name) in the order of the words the table shows for them in
 * `language`. Undefined for the other columns, which sort on their own values.
 */
export function activitySortOrder(
  key: string | undefined,
  actions: readonly string[],
  language: Language,
): string[] | undefined {
  return key === "action"
    ? labelOrder([...actions, ...ACTIVITY_ACTIONS], activityActionLabel, language)
    : undefined;
}
